// src/services/api.js
import axios from "axios";
import tokenManager from "../utils/tokenManager";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Calls that are made without a session (or that end it). A 401 from these
// is a real answer (e.g. wrong password), not an expired token.
const PUBLIC_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/logout",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/users/activate",
  "/invitations/accept",
  "/billing/plans",
];

const isPublicEndpoint = (url = "") =>
  PUBLIC_ENDPOINTS.some((path) => url.includes(path));

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    this.client.interceptors.request.use(
      async (config) => {
        if (!isPublicEndpoint(config.url) && tokenManager.shouldRefreshToken()) {
          try {
            await tokenManager.refreshAccessToken();
          } catch (error) {
            console.warn("Pre-request token refresh failed:", error?.message || error);
          }
        }

        const token = tokenManager.getAccessToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          if (!config.url.includes("/auth/") && !config.url.includes("/invitations/accept") && !config.url.includes("/invitations/")) {
            console.warn("No access token available for protected route:", config.url);
          }
        }

        if (["POST", "PUT", "DELETE", "PATCH"].includes(config.method.toUpperCase())) {
          const csrfToken = tokenManager.getCSRFToken();
          if (csrfToken) {
            config.headers["X-CSRF-Token"] = csrfToken;
          }
        }

        const fingerprint = await tokenManager.getFingerprint();
        if (fingerprint) {
          config.headers["X-Fingerprint"] = fingerprint;
        }

        if (!(config.data instanceof FormData)) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        // Any 401 on a protected call: try to renew the session once and
        // replay the request. If the session can't be renewed, tokenManager
        // ends it and the app redirects to /login.
        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !isPublicEndpoint(originalRequest.url)
        ) {
          originalRequest._retry = true;
          try {
            const newToken = await tokenManager.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            return Promise.reject(error);
          }
        }

        if (error.response?.data?.code === "SESSION_TIMEOUT") {
          tokenManager.endSession("timeout");
        }

        // The company's account is read only (trial ended, payment failed,
        // subscription ended). AppContext shows why and refreshes the banner.
        if (error.response?.status === 402 && error.response?.data?.code === "SUBSCRIPTION_READ_ONLY") {
          window.dispatchEvent(
            new CustomEvent("subscriptionReadOnly", {
              detail: { message: error.response.data.message, reason: error.response.data.reason },
            })
          );
        }

        // This person has been deactivated or archived
        if (error.response?.data?.code === "ACCOUNT_INACTIVE") {
          tokenManager.endSession?.("inactive");
        }

        if (error.response?.data?.code === "ACCOUNT_LOCKED") {
          window.dispatchEvent(
            new CustomEvent("accountLocked", {
              detail: { message: error.response.data.message },
            })
          );
        }

        return Promise.reject(error);
      }
    );
  }

  // ============= AUTH METHODS =============
  async register(userData) {
    try {
      const response = await this.client.post("/auth/register", userData);
      if (response.success) {
        const { accessToken, refreshToken, user } = response.data;
        tokenManager.setTokens(accessToken, refreshToken);
        if (response.data.fingerprint) tokenManager.setFingerprint(response.data.fingerprint);
        if (user) tokenManager.setUser(user);
      }
      return response;
    } catch (error) {
      console.error("Register API error:", error);
      throw error;
    }
  }

  async login(email, password, remember = false) {
    const fingerprint = await tokenManager.getFingerprint();
    const response = await this.client.post("/auth/login", { email, password, remember, fingerprint });
    if (response.success) {
      const { accessToken, refreshToken, user } = response.data;
      tokenManager.setTokens(accessToken, refreshToken);
      if (response.data.fingerprint) tokenManager.setFingerprint(response.data.fingerprint);
      if (user) tokenManager.setUser(user);
    }
    return response;
  }

  async logout(allDevices = false) {
    const refreshToken = tokenManager.getRefreshToken();
    try {
      await this.client.post("/auth/logout", { refreshToken, allDevices });
    } finally {
      if (allDevices) {
        tokenManager.clearAll();
      } else {
        tokenManager.clearCurrentSession();
      }
    }
  }

  async getMe() { return this.client.get("/auth/me"); }
  async forgotPassword(email) { return this.client.post("/auth/forgot-password", { email }); }
  async resetPassword({ token, password }) { return this.client.post("/auth/reset-password", { token, password }); }
  async changePassword(data) { return this.client.post("/auth/change-password", data); }
  async updatePersonalDetails(data) { return this.client.put("/auth/update-profile", data); }
  async uploadProfileImage(formData) { return this.client.post("/auth/upload-profile-image", formData); }
  async getSessions() { return this.client.get("/auth/sessions"); }
  async revokeSession(sessionId) { return this.client.delete(`/auth/sessions/${sessionId}`); }

  // ============= USER MANAGEMENT =============
  async getCompanyUsers(params = {}) { return this.client.get("/users", { params }); }
  async getUserById(userId) { return this.client.get(`/users/${userId}`); }
  async deleteUser(userId) { return this.client.delete(`/users/${userId}`); }
  async updateUserRole(userId, role) { return this.client.patch(`/users/${userId}/role`, { role }); }
  // status: 'active' | 'deactivated' | 'archived' — inactive people can't sign in and aren't billed
  async updateUserStatus(userId, status) { return this.client.patch(`/users/${userId}/status`, { status }); }

  /**
   * Create a new user directly (no invite flow).
   * POST /api/users
   * Body: { firstName, lastName, email, username?, password, role?, departmentId? }
   * Returns: { success, data: { user } }
   */
  async createUser(data) {
    return this.client.post("/users", data);
  }

  /**
   * Send a welcome invite email to an already-created user.
   * POST /api/users/:userId/invite
   * Returns: { success, message }
   */
  async sendUserInvite(userId) {
    return this.client.post(`/users/${userId}/invite`);
  }
  // Validates an activation token — returns { firstName, lastName, email } or 400
  async validateActivationToken(token) {
    return this.client.get('/users/activate', { params: { token } });
  }
  
  // Submits the new password against the token to activate the account
  async activateAccount(data) {
    return this.client.post('/users/activate', data);
  }

  async getCompanySettings() { return this.client.get("/settings/company"); }
  async updateCompanySettings(data) { return this.client.put("/settings/company", data); }
  async updateUserAllowance(userId, data) { return this.client.patch(`/settings/company/user/${userId}/allowance`, data); }
  async getLeaveTypes() { return this.client.get("/settings/company/leave-types"); }
  async createLeaveType(data) { return this.client.post("/settings/company/leave-types", data); }
  async updateLeaveType(id, data) { return this.client.put(`/settings/company/leave-types/${id}`, data); }
  async deleteLeaveType(id) { return this.client.delete(`/settings/company/leave-types/${id}`); }

  // Office shutdowns: adding one books the closed days off for everyone it applies to
  async getShutdowns() { return this.client.get("/settings/company/shutdowns"); }
  async previewShutdown(data) { return this.client.post("/settings/company/shutdowns/preview", data); }
  async createShutdown(data) { return this.client.post("/settings/company/shutdowns", data); }
  async updateShutdown(id, data) { return this.client.put(`/settings/company/shutdowns/${id}`, data); }
  async resyncShutdown(id) { return this.client.post(`/settings/company/shutdowns/${id}/resync`); }
  async deleteShutdown(id) { return this.client.delete(`/settings/company/shutdowns/${id}`); }

  // ============= TEAM METHODS =============
  async getTeams(params = {}) { return this.client.get("/teams", { params }); }
  async getTeam(teamId) { return this.client.get(`/teams/${teamId}`); }
  async createTeam(teamData) { return this.client.post("/teams", teamData); }
  async updateTeam(teamId, teamData) { return this.client.put(`/teams/${teamId}`, teamData); }
  async deleteTeam(teamId) { return this.client.delete(`/teams/${teamId}`); }
  async getTeamMembers(teamId) { return this.client.get(`/teams/${teamId}/members`); }
  async addTeamMember(teamId, userId, role = "member") { return this.client.post(`/teams/${teamId}/members`, { userId, role }); }
  async updateTeamMember(teamId, memberId, data) { return this.client.patch(`/teams/${teamId}/members/${memberId}`, data); }
  async removeTeamMember(teamId, memberId) { return this.client.delete(`/teams/${teamId}/members/${memberId}`); }
  async inviteToTeam(teamId, email, role = "member", message = "") { return this.client.post(`/teams/${teamId}/invite`, { email, role, message }); }

  // ============= INVITATION METHODS =============
  async sendInvitations(emails, role = "member", message = "", teamId = null) { return this.client.post("/invitations/send", { emails, role, message, teamId }); }
  async validateInvitationToken(token) { return this.client.get(`/invitations/${token}`); }
  async acceptInvitation(data) { return this.client.post("/invitations/accept", data); }
  async listInvitations(params = {}) { return this.client.get("/invitations", { params }); }
  async cancelInvitation(id) { return this.client.delete(`/invitations/${id}`); }
  async resendInvitation(id) { return this.client.post(`/invitations/${id}/resend`); }

  // ============= DEPARTMENT METHODS =============
  async getDepartments(params = {}) { return this.client.get("/departments", { params }); }
  async getDepartment(departmentId) { return this.client.get(`/departments/${departmentId}`); }
  async createDepartment(data) { return this.client.post("/departments", data); }
  async updateDepartment(departmentId, data) { return this.client.put(`/departments/${departmentId}`, data); }
  async deleteDepartment(departmentId) { return this.client.delete(`/departments/${departmentId}`); }
  async getDepartmentMembers(departmentId) { return this.client.get(`/departments/${departmentId}/members`); }
  async addDepartmentMember(departmentId, userId, role = "member") { return this.client.post(`/departments/${departmentId}/members`, { userId, role }); }
  async updateDepartmentMemberRole(departmentId, userId, role) { return this.client.patch(`/departments/${departmentId}/members/${userId}`, { role }); }
  async removeDepartmentMember(departmentId, userId) { return this.client.delete(`/departments/${departmentId}/members/${userId}`); }

  // ============= LEAVE REQUESTS =============
  async createLeaveRequest(data) { return this.client.post('/leave-requests', data); }
  async getLeaveRequests(params = {}) { return this.client.get('/leave-requests', { params }); }
  async deleteLeaveRequest(id) { return this.client.delete(`/leave-requests/${id}`); }
  // A 409 with code OVER_BALANCE means approving would take the person beyond
  // their balance: ask, then call again with { allowOverdraw: true }
  async approveLeaveRequest(id, note = '', opts = {}) { return this.client.patch(`/leave-requests/${id}/approve`, { note, ...opts }); }
  // Who I can approve: { all } for company admins, else { isDepartmentAdmin, departments, userIds }
  async getApprovalScope() { return this.client.get('/leave-requests/approvals/scope'); }
  // Requests from the people I can approve (plus my own)
  async getApprovalRequests(params = {}) { return this.client.get('/leave-requests/approvals', { params }); }
  async rejectLeaveRequest(id, note = '') { return this.client.patch(`/leave-requests/${id}/reject`, { note }); }
  // Booking preview (days, balance after, rules, holidays, clashes, sick note, approver) — nothing saved
  async previewLeaveRequest(data) { return this.client.post('/leave-requests/preview', data); }
  // The counts every screen shows (waiting for me, out today, approved this year, my approval queue)
  async getLeaveSummary(params = {}) { return this.client.get('/leave-requests/summary', { params }); }
  // Withdraw your own request / cancel someone else's (kept, with an audit trail)
  async withdrawLeaveRequest(id, note = '') { return this.client.patch(`/leave-requests/${id}/cancel`, { note }); }
  async getLeaveRequestHistory(id) { return this.client.get(`/leave-requests/${id}/history`); }
  // Supporting document (sick note): the person and HR only
  async uploadLeaveDocument(id, file) {
    const fd = new FormData();
    fd.append('file', file);
    return this.client.post(`/leave-requests/${id}/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  async getLeaveDocument(id) { return this.client.get(`/leave-requests/${id}/document`, { responseType: 'blob' }); }
  // Everyone who can be a named approver (admins and department managers)
  async getPossibleApprovers() { return this.client.get('/employees/approvers'); }

  // ============= PUBLIC HOLIDAYS =============
  async getPublicHolidays(year) { return this.client.get('/public-holidays', { params: { year } }); }
  async createPublicHoliday(data) { return this.client.post('/public-holidays', data); }
  async updatePublicHoliday(id, data) { return this.client.put(`/public-holidays/${id}`, data); }
  async deletePublicHoliday(id) { return this.client.delete(`/public-holidays/${id}`); }
  async importUKPublicHolidays(year) { return this.client.post('/public-holidays/import-uk', { year }); }
  async getHolidayExclusions(holidayId) { return this.client.get(`/public-holidays/${holidayId}/exclusions`); }
  async addHolidayExclusion(holidayId, userId) { return this.client.post(`/public-holidays/${holidayId}/exclusions`, { userId }); }
  async removeHolidayExclusion(holidayId, userId) { return this.client.delete(`/public-holidays/${holidayId}/exclusions/${userId}`); }
  async getEffectiveHolidaysForUser(userId, year) { return this.client.get(`/public-holidays/user/${userId}/effective`, { params: { year } }); }

  // ============= PUBLIC HOLIDAY LOCATIONS (multi-country) =============
  // Countries & regions
  async getHolidayCountries() { return this.client.get('/holiday-locations/countries'); }
  async getHolidayCountryRegions(countryCode) { return this.client.get(`/holiday-locations/countries/${countryCode}/regions`); }

  // Locations
  async getHolidayLocations(year) { return this.client.get('/holiday-locations', { params: { year } }); }
  async createHolidayLocation(data) { return this.client.post('/holiday-locations', data); }
  async deleteHolidayLocation(locationId) { return this.client.delete(`/holiday-locations/${locationId}`); }
  async setDefaultHolidayLocation(locationId) { return this.client.patch(`/holiday-locations/${locationId}/default`); }

  // Holidays within a location
  async getLocationHolidays(locationId, year) { return this.client.get(`/holiday-locations/${locationId}/holidays`, { params: { year } }); }
  async createLocationHoliday(locationId, data) { return this.client.post(`/holiday-locations/${locationId}/holidays`, data); }
  async updateLocationHoliday(locationId, holidayId, data) { return this.client.put(`/holiday-locations/${locationId}/holidays/${holidayId}`, data); }
  async deleteLocationHoliday(locationId, holidayId) { return this.client.delete(`/holiday-locations/${locationId}/holidays/${holidayId}`); }
  async importLocationHolidays(locationId, years) { return this.client.post(`/holiday-locations/${locationId}/import`, { years }, { timeout: 60000 }); }

  // People in a location
  async getHolidayLocationMembers(locationId) { return this.client.get(`/holiday-locations/${locationId}/members`); }
  async setHolidayLocationMembers(locationId, userIds, moveRemovedTo = null) { return this.client.put(`/holiday-locations/${locationId}/members`, { userIds, moveRemovedTo }); }

  // ============= EMPLOYEE DRAWER =============
  async getEmployeeProfile(userId) { return this.client.get(`/employees/${userId}/profile`); }
  async updateEmployeeProfile(userId, data) { return this.client.patch(`/employees/${userId}/profile`, data); }
  async getEmployeeSchedules(userId) { return this.client.get(`/employees/${userId}/schedules`); }
  async createEmployeeSchedule(userId, data) { return this.client.post(`/employees/${userId}/schedules`, data); }
  async updateEmployeeSchedule(userId, scheduleId, data) { return this.client.patch(`/employees/${userId}/schedules/${scheduleId}`, data); }
  async getEmployeeAllowance(userId, year) { return this.client.get(`/employees/${userId}/allowances`, { params: { year } }); }
  async upsertEmployeeAllowance(userId, data) { return this.client.put(`/employees/${userId}/allowances`, data); }
  async deleteEmployeeSchedule(userId, scheduleId) { return this.client.delete(`/employees/${userId}/schedules/${scheduleId}`); }

  // ============= CALENDAR SYNC =============
  // Subscription links (any calendar app) and direct Outlook / Google connections
  async getCalendarSettings() { return this.client.get('/calendar/settings'); }
  async resetCalendarFeeds() { return this.client.post('/calendar/feeds/reset'); }
  // Returns { url } — send the browser there to sign in to Microsoft / Google
  async connectCalendar(provider) { return this.client.post(`/calendar/connect/${provider}`); }
  async updateCalendarConnection(id, data) { return this.client.patch(`/calendar/connections/${id}`, data); }
  async syncCalendarConnection(id) { return this.client.post(`/calendar/connections/${id}/sync`, {}, { timeout: 120000 }); }
  async disconnectCalendar(id) { return this.client.delete(`/calendar/connections/${id}`, { timeout: 120000 }); }

  // ============= BILLING =============
  // Banner state for everyone: { access: { state: 'full' | 'read_only', reason, trialDaysLeft }, … }
  // ── Platform owner console (every customer; super admin only) ──
  async getPlatformAccess() { return this.client.get('/platform/access'); }
  async getPlatformOverview(params = {}) { return this.client.get('/platform/overview', { params }); }
  async getPlatformCompanies() { return this.client.get('/platform/companies'); }
  async getPlatformCompany(id) { return this.client.get(`/platform/companies/${id}`); }
  async getPlatformActivity(params = {}) { return this.client.get('/platform/activity', { params }); }

  async getBillingStatus() { return this.client.get('/billing/status'); }
  // Admins: plan, trial, active members, price list for the company's market
  async getBillingSummary() { return this.client.get('/billing/summary'); }
  // Public price list for a country (GB / AU / ZA …)
  async getBillingPlans(country) { return this.client.get('/billing/plans', { params: { country } }); }
  async validateBillingCode(code) { return this.client.post('/billing/validate-code', { code }); }
  // Returns { url } — send the browser there to pay
  async startCheckout({ interval, code }) { return this.client.post('/billing/checkout', { interval, code }); }
  async openBillingPortal() { return this.client.post('/billing/portal'); }
  async cancelSubscription() { return this.client.post('/billing/cancel'); }
  async resumeSubscription() { return this.client.post('/billing/resume'); }
  async changeBillingInterval(interval) { return this.client.post('/billing/interval', { interval }); }
  async changeBillingCountry(billingCountry, region = null) { return this.client.put('/billing/country', { billingCountry, region }); }
  async syncBilling(sessionId) { return this.client.post('/billing/sync', sessionId ? { sessionId } : {}); }

  // ============= LEAVE POLICIES & BALANCES =============
  async getLeavePolicies() { return this.client.get('/leave-policies'); }
  async getLeavePolicy(id) { return this.client.get(`/leave-policies/${id}`); }
  async getLeavePolicyTemplates() { return this.client.get('/leave-policies/templates'); }
  async createLeavePolicy(data) { return this.client.post('/leave-policies', data); }
  async updateLeavePolicy(id, data) { return this.client.put(`/leave-policies/${id}`, data); }
  async deleteLeavePolicy(id) { return this.client.delete(`/leave-policies/${id}`); }
  async setDefaultLeavePolicy(id) { return this.client.post(`/leave-policies/${id}/default`); }
  async setLeavePolicyMembers(id, userIds) { return this.client.put(`/leave-policies/${id}/members`, { userIds }); }
  async setDepartmentLeavePolicy(departmentId, leavePolicyId) { return this.client.put(`/leave-policies/departments/${departmentId}`, { leavePolicyId }); }
  // { [userId]: { balance, policy } } — admins get everyone, members themselves
  async getLeaveBalances(params = {}) { return this.client.get('/leave-balances', { params }); }
  async getLeaveBalance(userId, params = {}) { return this.client.get(`/leave-balances/${userId}`, { params }); }
  async getBalanceAdjustments(userId) { return this.client.get(`/leave-balances/${userId}/adjustments`); }
  async addBalanceAdjustment(userId, data) { return this.client.post(`/leave-balances/${userId}/adjustments`, data); }
  async deleteBalanceAdjustment(userId, id) { return this.client.delete(`/leave-balances/${userId}/adjustments/${id}`); }
  // { date, unit, rows: [{ email, amount }] }
  async importOpeningBalances(data) { return this.client.post('/leave-balances/import', data); }

  // ============= PASSWORD METHODS =============
  async getPasswords(params = {}) { return this.client.get("/passwords", { params }); }
  async getPassword(passwordId) { return this.client.get(`/passwords/${passwordId}`); }
  async createPassword(passwordData) { return this.client.post("/passwords", passwordData); }
  async updatePassword(passwordId, passwordData) { return this.client.put(`/passwords/${passwordId}`, passwordData); }
  async deletePassword(passwordId) { return this.client.delete(`/passwords/${passwordId}`); }
  async getPasswordStats() { return this.client.get("/passwords/stats"); }
  async getPasswordCategories() { return this.client.get("/passwords/categories"); }
  async generateRandomPassword(options = {}) { return this.client.post("/passwords/generate", options); }
  async checkPasswordStrength(password) { return this.client.post("/passwords/check-strength", { password }); }
  async decryptPassword(passwordId, data) { return this.client.post(`/passwords/${passwordId}/decrypt`, data); }
  async exportPasswords(format = "json") { return this.client.post("/passwords/export", { format }, { responseType: "blob" }); }
  async importPasswords(file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.client.post("/passwords/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
  }
  async toggleFavorite(passwordId, isFavorite) { return this.client.patch(`/passwords/${passwordId}/favorite`, { isFavorite }); }
  async searchPasswords(query) { return this.client.get(`/passwords/search/${encodeURIComponent(query)}`); }
  async getRecentPasswords(limit = 10) { return this.client.get("/passwords/recent", { params: { limit } }); }
  async getByWebsite(website) { return this.client.get(`/passwords/website/${encodeURIComponent(website)}`); }
}


const apiService = new ApiService();
export default apiService;